import { hasValue } from '@woonstad/helpers-utils';
import { DeepRequired, FieldError, FieldErrors, FieldErrorsImpl, FieldValues, Merge } from 'react-hook-form';

export function handleInvalidForm<TFieldValues extends FieldValues>(errors: FieldErrors<TFieldValues>, event?: React.BaseSyntheticEvent, scrollOffset = -50) {
  const form = event?.target;

  // Handle error on super refinements
  // these errors are of type custom
  //     => find the errors of type "custom" and is usually located on the key root
  //     => check if this is the first error we encounter in the form
  //     => find the first field related to the error
  //     => set the focus to this element
  const errorList = listErrors(errors);
  const errorOrder = getFormElementOrder(errorList, form);
  const firstError = errorOrder[0];

  // If the firs element is a custom error => focus there
  if (hasValue(firstError)) {
    const field = form.querySelector(`[name^="${firstError.key}"]`);

    // For radio / checkbox focus the button before the input added by Radix-ui
    if (field?.tagName === 'SELECT') {
      const boundingRect = field.previousSibling.getBoundingClientRect();

      window.scrollTo({
        top: scrollOffset + window.scrollY + boundingRect.top,
        behavior: 'smooth',
      });
    } else if (field?.type === 'file') {
      // Focus the label for file uploads...
      const parent = field.closest('[role=presentation]');
      const label = parent.querySelector('label');
      const boundingRect = label.getBoundingClientRect();

      window.scrollTo({
        top: scrollOffset + window.scrollY + boundingRect.top,
        behavior: 'smooth',
      });
    } else if (hasValue(field)) {
      // else focus the input
      field?.focus();
    }
  }
}

type CustomFieldError = FieldError & { key: string };

// Get the order of errors in the form based on the location of the elements in the form
function getFormElementOrder(errors: CustomFieldError[], form: HTMLFormElement) {
  // Find all form elements in the form
  const elements = form.querySelectorAll('input, select, textarea');
  const errorList: Array<CustomFieldError & { order: number; }> = [];

  // Loop over all elements and check if we have a error for this element.
  elements.forEach((el, index) => {
    const elementName = el.getAttribute('name');
    const fieldError = errors.find((error) => error.key === elementName);
    const isKnownError = errorList.some((error) => error.key === fieldError?.key);

    if (hasValue(fieldError) && !isKnownError) {
      errorList.push({
        order: index,
        ...fieldError,
      });
    }
  });

  // Then sort them based on this order
  return errorList.sort((a, b) => {
    // no order => to the bottom
    if (!hasValue(a.order) || !hasValue(b.order)) {
      return -1;
    }

    if (a.order === b.order) {
      return 0;
    }

    return a?.order - b?.order;
  });
}

// Create a list of errors from a object of errors
function listErrors<T extends FieldValues = FieldValues>(errors: FieldErrors<T>, parentKey?: string) {
  const errorFields = Object.keys(errors);
  const errorMessages: Array<CustomFieldError> = [];

  errorFields.forEach((errorKey) => {
    const fieldError = errors[errorKey];
    const fieldPath = `${hasValue(parentKey) ? `${parentKey}.` : ''}${errorKey}`;

    if (fieldError !== undefined) {
      if (isErrorObject<T>(fieldError)) {
        errorMessages.push({
          ...fieldError,
          key: fieldPath,
        });
      } else {
        // recursion to find more errors
        errorMessages.push(...listErrors<T>(fieldError as FieldErrorsImpl<DeepRequired<T>[string]>, fieldPath));
      }
    }
  });

  return errorMessages;
}

// Check if the element is a error
function isErrorObject<T extends FieldValues>(error: FieldError | Merge<FieldError, FieldErrorsImpl<DeepRequired<T>[string]>> | undefined): error is FieldError {
  if (!hasValue(error)) {
    return false;
  }

  return 'message' in error && 'ref' in error && 'type' in error;
}
